import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Input, Switch, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useFilterGenericContent } from '../hooks/useFilterGenericContent'
import {
  FilterGenericContentForm,
  FilterGenericContentSchema,
} from '../schemas/FilterGenericContentSchema'

export function DrawerFilterGenericContent() {
  const { t } = useTranslation(['settings', 'common'])
  const { close } = useDrawer('drawer-filter-generic-content')
  const [_, setFilters] = useFilterGenericContent()
  const hookForm = useForm<FilterGenericContentForm>({
    resolver: zodResolver(FilterGenericContentSchema()),
    defaultValues: {
      freeSearch: null,
      contentType: null,
      relativeKeys: null,
      searchTags: null,
      isActive: false,
    },
  })

  const handleSubmit = (data: FilterGenericContentForm) => {
    setFilters(data)
    close()
  }

  return (
    <Drawer.Root id="drawer-filter-generic-content">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('generic_content.drawer_filter.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('generic_content.drawer_filter.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Icon
              icon={<Search />}
              name="freeSearch"
              label={t(
                'generic_content.drawer_filter.fields.free_search.label',
              )}
              placeholder={t(
                'generic_content.drawer_filter.fields.free_search.placeholder',
              )}
            />

            <Input.Icon
              icon={<Search />}
              name="contentType"
              label={t(
                'generic_content.drawer_filter.fields.content_type.label',
              )}
              placeholder={t(
                'generic_content.drawer_filter.fields.content_type.placeholder',
              )}
            />

            <Input.Icon
              icon={<Search />}
              name="relativeKeys"
              label={t(
                'generic_content.drawer_filter.fields.relative_keys.label',
              )}
              placeholder={t(
                'generic_content.drawer_filter.fields.relative_keys.placeholder',
              )}
            />

            <Input.Icon
              icon={<Search />}
              name="searchTags"
              label={t(
                'generic_content.drawer_filter.fields.search_tags.label',
              )}
              placeholder={t(
                'generic_content.drawer_filter.fields.search_tags.placeholder',
              )}
            />

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="isActive">
                {t('generic_content.drawer_filter.fields.is_active.label')}
              </Switch.Label>
              <Switch.Trigger name="isActive" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-between gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={close}
            type="button"
          >
            {t('generic_content.drawer_filter.footer.button_close.label')}
          </Button.Root>

          <div className="flex items-center gap-4">
            <Button.Root
              variant="secondary"
              color="danger"
              onClick={() => hookForm.reset()}
            >
              {t('generic_content.drawer_filter.footer.reset_button.label')}
            </Button.Root>

            <Button.Root
              variant="primary"
              color="brand"
              onClick={hookForm.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('generic_content.drawer_filter.footer.submit_button.label')}
            </Button.Root>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
